import { Avatar, Box, Button, CircularProgress, Grid, ImageList, ImageListItem, Stack, SxProps, TextField, Typography } from "@mui/material"
import React, { useState } from "react"
import { CustomSX, RHFField } from "../../../interface/FormGenerator"
import { useMutation, useQuery } from "react-query"
import { apiClient } from "@/apiClient/apiService"
import API_END_POINT, { ROUTES } from "@/apiClient/apiEndpoints"
import { useSearchParams } from "next/navigation"
import { COLORS } from "@/theme/localTheme"
import dayjs from "dayjs"
import { useForm } from "react-hook-form"
import { toast } from "react-toastify"
import useUserDetails from "@/hooks/useUserDetails"
import SendIcon from "@mui/icons-material/Send"
import CloseIcon from "@mui/icons-material/Close"
import { TImageModal } from "../../../interface/ImagePreview"
import ImagePreview from "@/components/layout-components/ImagePreview"
import AddImage from "@/components/field-components/AddImage"
import AsyncImage from "@/components/common/AsyncImage"
import { extractImageTimeStamp, extractImageUrl, responseToImagePreview } from "@/utils/CommonFunctions"

interface postCommentData {
  [x: string]: number | string | any
  comment_text: string
  url: any[]
  user_id: number | undefined
}

const TextFieldSX: CustomSX = {
  enable: {
    "& .MuiOutlinedInput-input": {
      color: COLORS.textOffWhite,
    },
    "& fieldset": {
      borderColor: COLORS?.adminAppOutlineColor,
    },

    "& .MuiInputBase-root.Mui-focused fieldset": {
      "&:hover fieldset": {
        borderColor: COLORS?.adminAppOutlineColor,
      },
      borderColor: COLORS?.adminAppOutlineColor,
    },
    "& .MuiInputBase-root:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline ": {
      borderColor: COLORS?.adminAppOutlineColor,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: COLORS.textOffWhite,
    },
  },
  disable: {
    "& .MuiOutlinedInput-input": {
      color: COLORS.textOffWhite,
    },
    "& fieldset": {
      borderColor: COLORS?.adminAppTextPrimary,
    },
    "&:hover fieldset": {
      borderColor: COLORS?.adminAppTextPrimary,
    },
    "& .MuiInputBase-root.Mui-focused fieldset": {
      "&:hover fieldset": {
        borderColor: COLORS?.adminAppTextPrimary,
      },
      borderColor: COLORS?.adminAppTextPrimary,
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline ": {
      borderColor: COLORS?.adminAppTextPrimary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: COLORS.textOffWhite,
    },
  },
  common: {
    '& .MuiInputBase-root[prefix="$"] input': {
      paddingLeft: "5px",
    },
    "& .MuiInputBase-root[data-rtl] input": {
      textAlign: "right",
    },
  },
}

function CommentsSection({ identifier = "ticket_id" }: { identifier?: "ticket_id" | "clean_id" }) {
  const [commentRequired, setCommentRequired] = useState(false)
  const [parentSelectedFiles, setParentSelectedFiles] = useState<any[]>([])
  const [prevImage, setPrevImage] = useState<TImageModal>({ isOpen: false, images: [], startIndex: 0 })

  const { data: userDetailsData } = useUserDetails()
  const userData = userDetailsData?.data?.data

  const searchParams = useSearchParams()
  const ticketId: number = Number(searchParams.get("id"))

  const { data: allComments, refetch } = useQuery({
    queryKey: ["formTicketComments"],
    queryFn: () => {
      return apiClient.get(`${API_END_POINT.GET[ROUTES.getAllComments]}?${identifier}=${ticketId}`)
    },
  })
  //@ts-ignore
  const previousComments = allComments?.data?.comments

  const postCommentmutation = useMutation({
    mutationFn: (data: postCommentData) => {
      return apiClient.post(API_END_POINT.POST[ROUTES.postComments], data)
    },
    onSuccess: () => {
      toast.success("Comment Added", {
        position: "top-right",
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
      reset()
      refetch()
      setParentSelectedFiles([])
    },
  })

  const { register, handleSubmit, reset } = useForm<postCommentData>()

  const postComment = async (data: postCommentData) => {
    if (data?.comment_text != "") {
      setCommentRequired(false)
      const commentPayload = {
        [identifier]: ticketId,
        comment_text: data?.comment_text,
        user_id: userData?.id,
        url: parentSelectedFiles,
      }
      await postCommentmutation.mutateAsync(commentPayload)
    } else {
      setCommentRequired(true)
    }
  }

  const deleteFile = (index: number) => {
    const updatedSelectedFiles = [...parentSelectedFiles]
    updatedSelectedFiles.splice(index, 1)

    setParentSelectedFiles(updatedSelectedFiles)
  }

  const handleFilesSelected = (selectedFiles: any[]) => {
    setParentSelectedFiles(selectedFiles)
  }

  const handleOpenImagePreview = (images: Array<any>, index: number) => {
    const imageList = images.map(responseToImagePreview)
    setPrevImage({
      isOpen: true,
      images: imageList,
      startIndex: index,
    })
  }

  const handleCloseImagePreview = () => {
    setPrevImage({
      isOpen: false,
      images: [],
      startIndex: 0,
    })
  }

  return (
    <Stack gap={1}>
      <Box>
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <TextField
                label="Final Comments"
                multiline
                rows={4}
                fullWidth
                sx={[{ ...TextFieldSX.enable }, { ...TextFieldSX.common }] as SxProps}
                InputLabelProps={{
                  style: { color: COLORS?.adminAppTextPrimary },
                }}
                {...register("comment_text", {
                  minLength: {
                    value: 1,
                    message: "Comment is required.",
                  },
                })}
              />
              {commentRequired && <Typography sx={{ color: COLORS.errorState }}>Comment is required</Typography>}
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} pt={5}>
            <AddImage
              onFilesSelected={handleFilesSelected}
              parentSelectedFilesLength={parentSelectedFiles.length}
              btnVariant="outlined"
              btnSxProps={{ color: COLORS.textWhite }}
            />
            <Button
              variant="contained"
              onClick={handleSubmit(postComment)}
              disabled={postCommentmutation.isLoading}
              sx={{
                backgroundColor: COLORS.primaryButton,
              }}
              startIcon={postCommentmutation.isLoading ? <CircularProgress size="1.4rem" /> : <SendIcon />}
            >
              Add Comment
            </Button>
          </Stack>
          <Box
            sx={{
              marginRight: "2rem",
              display: "flex",
              gap: "2%",
              mt: "1.5rem",
            }}
          >
            {parentSelectedFiles?.length > 0 && (
              <ImageList
                sx={{
                  gridAutoFlow: "column",
                  gridTemplateColumns: "repeat(auto-fit, minmax(96px,1fr)) !important",
                  gridAutoColumns: "minmax(96px, 1fr)",
                  "&::-webkit-scrollbar ": {
                    display: "block",
                    height: "8px",
                    width: "8px",
                    background: "transparent",
                  },

                  "&::-webkit-scrollbar-thumb": {
                    background: "#ffffff75",

                    "&::-webkit-border-radius": "1px",
                    "&::-webkit-box-shadow": "0px 1px 2px rgba(0, 0, 0, 0.75)",
                  },
                  "&::-webkit-scrollbar-corner": {
                    background: "#000",
                  },
                }}
              >
                {parentSelectedFiles.map((attachment, index) => (
                  <ImageListItem key={index}>
                    <Box onClick={() => handleOpenImagePreview(parentSelectedFiles, index)}>
                      <AsyncImage
                        alt={`Attachment${index}`}
                        src={extractImageUrl(attachment)}
                        width={84}
                        height={84}
                        title={extractImageTimeStamp(attachment)}
                        style={{ borderRadius: "0.5rem", marginBottom: "4px" }}
                      />
                    </Box>
                    <CloseIcon
                      onClick={() => deleteFile(index)}
                      style={{
                        position: "absolute",
                        top: "0px",
                        right: "0px",
                        cursor: "pointer",
                        borderRadius: "50%",
                        padding: "3px",
                        color: "black",
                        backgroundColor: "white",
                      }}
                      fontSize="small"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>
        </form>

        <Box display={"flex"} flexDirection={"column"} gap={2} borderTop={`1px solid ${COLORS.divider}`} paddingTop={"1rem"} marginTop={"1rem"}>
          {previousComments?.map((data: any, index: number) => {
            const commentImages = data.url || []

            return (
              <Box key={index} display={"flex"} gap={2}>
                <Avatar
                  sx={{ width: 30, height: 30 }}
                  src={`https://ui-avatars.com/api/?background=1b920a&color=fff&name=${data?.user?.first_name}`}
                />
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  <Box display={"flex"} alignItems={"center"} justifyContent={"flex-start"} gap={2}>
                    <Typography fontSize={"16px"} fontWeight={"500"} letterSpacing={"0.15px"} color={COLORS.commentUserName}>
                      {data?.user?.first_name} {data?.user?.last_name}
                    </Typography>
                    <Typography fontSize={"12px"} fontWeight={"500"} letterSpacing={"0.15px"} color={COLORS?.adminAppTextPrimary}>
                      {dayjs(data.created_at, { locale: "en" }).format("DD-MM-YYYY hh:mm A")}
                    </Typography>
                  </Box>
                  <Typography color={COLORS?.textOffWhite}> {data.comment_text} </Typography>
                  <Box display={"flex"} gap={1}>
                    {commentImages.length >= 1 &&
                      commentImages.map((image: any, index: number) => (
                        <Box key={index} onClick={() => handleOpenImagePreview(commentImages, index)}>
                          <AsyncImage src={image} width={84} height={84} style={{ borderRadius: "0.5rem" }} alt={`Comment_Img${index}`} />
                        </Box>
                      ))}
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
      <ImagePreview startIndex={prevImage.startIndex} images={prevImage.images} isOpen={prevImage.isOpen} onClose={handleCloseImagePreview} />
    </Stack>
  )
}

export default CommentsSection
